import { Dropdown, Menu, Tooltip } from 'antd';
import Link from 'next/link';
import { useSession } from 'next-auth/react';
import { useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useIntercom } from 'react-use-intercom';
import { createEditor } from 'slate';
import { Slate, withReact } from 'slate-react';
import styled, { keyframes } from 'styled-components';

import {
  ArrowDownSvg,
  ArrowLeftSvg,
  CheckSvg,
  PlusSvg,
  QuestionMarkSvg,
} from '../../../assets/icons';
import BrainSvg from '../../../assets/icons/BrainSvg';
import CuripodAgentSvg from '../../../assets/icons/CuripodAgentSvg';
import CuripodAIStarsSvg from '../../../assets/icons/CuripodAIOrangeFilledSvg';
import CuripodAISolidStarsSvg from '../../../assets/icons/CuripodAISolidStarsSvg';
import FoldedHandsSVG from '../../../assets/icons/FoldedHands';
import HomeFilledSvg from '../../../assets/icons/HomeFilledSvg';
import { MagicBackground } from '../../../assets/icons/MagicBackground';
import MediaIconSvg from '../../../assets/icons/MediaIconSvg';
import PasteSvg from '../../../assets/icons/PasteSvg';
import { ShuffleSvg } from '../../../assets/icons/ShuffleSvg';
import UploadsSvg from '../../../assets/icons/UploadsSvg';
import YoutubeSvg from '../../../assets/icons/YoutubeSvg';
import theme from '../../../assets/theme';
import GameAPI from '../../../frontendServices/gameAPI';
import useAccessBadge from '../../../hooks/useAccessBadge';
import { useBreakPoint } from '../../../hooks/useBreakPoint';
import useConsumeGenericGeneratorAsNewPresentation from '../../../hooks/useConsumeGenericGeneratorAsNewPresentation';
import {
  ChatMessage,
  useDynamicLessonStateContext,
} from '../../../hooks/useCreateLessonState';
import { useMetrics } from '../../../hooks/useMetrics';
import { usePrevious } from '../../../hooks/usePrevious';
import useRouterMatch from '../../../hooks/useRouterMatch';
import { useTestPrepState } from '../../../hooks/useTestPrepState';
import SocketProvider from '../../../providers/SocketProvider/SocketProvider';
import { lessonCategories, writingSkills } from '../../../utils/createLessonPageUtils';
import { markdownToSlateEnhancedLearningObjective } from '../../../utils/richTextUtils';
import { Loader } from '../../_atoms/_Loader';
import { TextArea } from '../../_atoms/_TextArea';
import { Button } from '../../_atoms/Button';
import { CustomIntercomButton } from '../../_atoms/CustomIntercomButton';
import { HideNextJsLoader } from '../../_atoms/HideNextJsLoader';
import { IconWrapper } from '../../_atoms/IconWrapper';
import { LoadingDots } from '../../_atoms/StreamingLongAnswerList/StreamingLongAnswerList';
import BaseModal from '../../_molecules/BaseModal';
import ShowLessonCreationProgressBar from '../../_molecules/ProgressBar/ShowLessonCreationProgressBar';
import { Element, Leaf, SlateEditable } from '../../SlideComponents/slateUtil';
import { CenteredContainer } from '../../styled/CenteredContainer';
import { CategoryList } from './CategorySelector';
import { SubPageLayout } from './CreatePage';
import { CurifyTab } from './CurifyTab';
import { FindCategorizedLessonsTab } from './FindCategorizedLessonsTab';
import { useLearningObjectiveRefinement } from './hooks/useLearningObjectiveRefinementHook';
import { TestPrepTab } from './TestPrepTab';
import { Page } from './types';

const isSlate = (text: string) => {
  return text.startsWith('[{') && text.endsWith('}]');
};

const LessonReadyButtons = styled.div`
  position: absolute;
  bottom: 0px;
  top: 10px;
  right: 0px;
  width: 100%;
  height: 100px;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  flex-wrap: wrap;
`;

const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;
const GenerateLessonButton = styled(Button)`
  padding: 10px 20px;
  border: none;
  border-radius: 5px;
  background: linear-gradient(45deg, #ffa55b, #ffc107, #ffa55b, #ffc107);
  background-size: 600% 400%;
  animation: ${gradientShift} 6s ease infinite;
  color: ${theme.colors.white};
  box-shadow: ${theme.effects.floatingCreatorElementShadow};
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    background: linear-gradient(45deg, #ffa55b, #ffc107);
  }

  svg {
    height: 25px;
    width: 25px;
  }
`;

const ChatMessageViewer = ({ content }: { content: string }) => {
  const metrics = useMetrics();
  const editor = useMemo(() => withReact(createEditor()), []);
  const parsedContent = useMemo(
    () =>
      isSlate(content)
        ? JSON.parse(content)
        : markdownToSlateEnhancedLearningObjective(content, 16),
    [content],
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderElement = React.useCallback((props: any) => {
    // Ensure all elements have a minimum font size of 16px
    const elementWithSize = {
      ...props.element,
      size: 16,
    };
    return <Element {...props} element={elementWithSize} />;
  }, []);
  const renderLeaf = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (props: any) => {
      return <Leaf {...props} metrics={metrics} />;
    },
    [metrics],
  );

  useEffect(() => {
    editor.children = parsedContent;
    editor.onChange();
  }, [parsedContent, editor]);

  return (
    <SlateWrapper>
      <span translate="no">
        <Slate editor={editor} value={parsedContent}>
          <SlateEditable
            readOnly={true}
            $readOnly={true}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            style={{
              fontSize: 16,
              backgroundColor: 'transparent',
              padding: 0,
              margin: 0,
              border: 'none',
              boxShadow: 'none',
            }}
          />
        </Slate>
      </span>
    </SlateWrapper>
  );
};

const SlateWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  ul,
  ol {
    padding-inline-start: 10px;
  }
`;

const lessonSuggestions = [
  {
    summary: 'Making inferences',
    fullValue:
      '6th grade novel study on The Giver Chapter 3 focusing on making inferences.',
  },
  {
    summary: 'Sensory imagery',
    fullValue:
      '4th grade writing lesson. Use sensory imagery to convey a sequence of events from summer break.',
  },
  {
    summary: 'Counterclaims',
    fullValue:
      '9th grade ELA lesson on developing counterclaims. Focus on anticipating views of the intended audience. The topic should be high school electives.',
  },
  {
    summary: 'Two-step word problems',
    fullValue:
      '2nd grade math lesson on using subtraction within 100 to solve two-step word problems. Include problems about the playground.',
  },
  {
    summary: 'Proportions',
    fullValue:
      '7th grade math lesson on using proportions to solve real-world percent problems.',
  },
  {
    summary: 'Food webs',
    fullValue:
      '5th grade science lesson about the movement of matter between producers, consumers, and decomposers in food webs in the Everglades National Park.',
  },
  {
    summary: 'Photosynthesis',
    fullValue:
      '8th grade science lesson on photosynthesis focusing on the structure of the chloroplast.',
  },
  {
    summary: 'The American Revolution',
    fullValue:
      '10th grade U.S. history lesson. How did protests escalate tensions between Britain and the colonies leading up to the American Revolution?',
  },
  {
    summary: 'Early Hominids',
    fullValue:
      '6th grade social studies lesson on the capabilities that helped *Homo habilis* ("Handy Man") survive.',
  },
  {
    summary: 'Developing Empathy',
    fullValue:
      '3rd grade SEL lesson about recognizing that others may have different perspectives and experiences.',
  },
  {
    summary: 'Digital Citizenship',
    fullValue:
      '8th grade digital citizenship lesson. How do digital media tools try to hook you, and what can you do about it?',
  },
  {
    summary: 'CTE',
    fullValue:
      '11th grade CTE lesson on how to respond to commonly used interview questions for a job in hospitality.',
  },
];

const ObjectiveRefinementWithSocket = ({
  chatHistory,
  dispatchAndSync,
}: {
  chatHistory: ChatMessage[];
  dispatchAndSync: (action: any) => void;
}) => {
  const [inputValue, setInputValue] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isTextAreaFocused, setIsTextAreaFocused] = useState(false);
  const [isInputContainerHovered, setIsInputContainerHovered] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const { showNewMessages } = useIntercom();
  const chatScrollRef = useRef<HTMLDivElement>(null);
  const streamCleanupRef = useRef<(() => void) | null>(null);
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const scrollRef = useRef<HTMLDivElement>(null);
  const streamingMessageRef = useRef<HTMLDivElement>(null);
  const lastUserMessageRef = useRef<HTMLDivElement>(null);
  const [spacerHeight, setSpacerHeight] = useState('0px');
  const tenantSubjects = userAccessBadge?.user.profileInfo?.subjects;
  const tenantGrades = userAccessBadge?.user.profileInfo?.grades;
  const presetState = userAccessBadge?.organization?.region;
  const [showLoader, setShowLoader] = useState(false);
  const metrics = useMetrics();

  const previousAgentHistory = usePrevious(chatHistory);

  const {
    isStreaming,
    streamData,
    startStream,
    resetStream,
    error,
    stopStream,
    isObjectiveReady,
    promptVariables,
    sessionLength,
    setIsObjectiveReady,
  } = useLearningObjectiveRefinement();

  const { tenantId, router } = useRouterMatch();

  const {
    consumeGenerator,
    isLoading: isGeneratingLesson,
    isError,
    data,
    error: errorConsumingGenerator,
  } = useConsumeGenericGeneratorAsNewPresentation(tenantId);

  useEffect(() => {
    if (isGeneratingLesson || data) {
      setShowLoader(true);
      return;
    } else {
      setShowLoader(false);
      return;
    }
  }, [data, isGeneratingLesson, setShowLoader]);

  useEffect(() => {
    if (
      previousAgentHistory &&
      previousAgentHistory.length > 0 &&
      chatHistory.length === 0
    ) {
      // History was reset by the user.
      resetStream();
      setIsObjectiveReady(false);
      return;
    }

    if (
      chatHistory.length > 0 &&
      previousAgentHistory &&
      previousAgentHistory?.length >= 0
    ) {
      const lastAgentHistory = chatHistory[chatHistory.length - 1];
      const lastPreviousAgentHistory =
        previousAgentHistory[previousAgentHistory.length - 1];
      if (
        lastAgentHistory?.role === 'user' &&
        (lastPreviousAgentHistory?.role === 'assistant' || !lastPreviousAgentHistory)
      ) {
        const lastUserMessageHeight = lastUserMessageRef.current?.clientHeight || 65;
        setSpacerHeight(`calc(100% - ${lastUserMessageHeight + 35}px)`); // 46 one line chat message

        requestAnimationFrame(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        });
      }
    }
  }, [chatHistory, previousAgentHistory, resetStream, setIsObjectiveReady]);

  useEffect(() => {
    if (data) {
      const blockLabels = (data.content.slides || [])
        .filter((value, index, self) => self.indexOf(value) === index)
        .map(slide => slide.blockId?.split('-')[0])
        .filter(Boolean);

      metrics.logEvent('CreatePage.Generator.Consume.FullLessonSuccessRedirect', {
        generatorId: 'genericFullLesson',
        generatorName: 'genericFullLesson',
        standard: promptVariables?.standardAlignment,
        subject: promptVariables?.subject,
        grade: promptVariables?.grade,
        generatedLessonId: data.id,
        tenantSubjects: tenantSubjects,
        language: promptVariables?.languages?.[0] || 'English',
        tenantGrades: tenantGrades,
        schoolState: presetState,
        prompt: promptVariables?.standardAlignment
          ? `${promptVariables.learningObjective} ${promptVariables.standardAlignment}`
          : promptVariables?.learningObjective,
        skeleton: data.metadata?.skeleton,
        blocks: blockLabels,
        sessionLength: sessionLength,
      });

      router.push(`/${tenantId}/lessons/${data.id}/edit`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, tenantId, data]);

  // Randomly select 3 suggestions
  const [randomSuggestions, setRandomSuggestions] = useState(() => {
    const shuffled = [...lessonSuggestions].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 3);
  });

  const shuffleSuggestions = () => {
    const shuffled = [...lessonSuggestions].sort(() => 0.5 - Math.random());
    setRandomSuggestions(shuffled.slice(0, 3));
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamCleanupRef.current) {
        streamCleanupRef.current();
      }
    };
  }, []);

  // Reset stream when complete and add to chat history
  useEffect(() => {
    if (streamData.isComplete) {
      // Capture the content before resetting
      const completedContent = streamData.currentContent;

      // Add the completed assistant message to chat history
      if (completedContent) {
        const assistantMessage: ChatMessage = {
          role: 'assistant',
          content: completedContent,
          timestamp: new Date(),
        };
        dispatchAndSync({ type: 'ADD_CHAT_MESSAGE', payload: assistantMessage });
      }

      // Reset immediately to prevent double rendering
      resetStream();
    }
  }, [streamData.isComplete, resetStream, streamData.currentContent, dispatchAndSync]);

  // Handle successful lesson generation
  useEffect(() => {
    if (data) {
      router.push(`/${tenantId}/lessons/${data.id}/edit`);
    }
  }, [data, router, tenantId]);

  const handleSuggestionClick = (fullValue: string) => {
    setInputValue(fullValue);
  };

  const handleSubmit = async () => {
    if (!inputValue.trim()) return;
    if (chatHistory.length === 0) {
      metrics.logEvent('CreatePage.SubmitFirstLessonPrompt', {
        lessonPrompt: inputValue,
      });
    } else {
      metrics.logEvent('CreatePage.SubmitLessonPrompt', {
        lessonPrompt: inputValue,
        chatHistoryLength: chatHistory.length,
      });
    }

    // Add user message to chat history immediately
    const userMessage: ChatMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date(),
    };
    dispatchAndSync({ type: 'ADD_CHAT_MESSAGE', payload: userMessage });

    // Start the stream with just the message
    const cleanup = startStream(inputValue);
    streamCleanupRef.current = cleanup || null;

    setInputValue('');
  };

  useEffect(() => {
    if (isError) {
      setShowErrorModal(true);
    }
  }, [isError]);

  // Watch streaming message height and adjust spacer
  useEffect(() => {
    if (!streamingMessageRef.current || !isStreaming || chatHistory.length === 0) {
      return;
    }

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        const messageHeight = entry.contentRect.height;
        const lastUserMessageHeight = lastUserMessageRef.current?.clientHeight || 65;
        const baseHeight =
          (chatScrollRef.current?.clientHeight || 0) - (lastUserMessageHeight + 25);

        const newSpacerHeight = Math.max(0, baseHeight - messageHeight);
        setSpacerHeight(`${newSpacerHeight}px`);
      }
    });

    resizeObserver.observe(streamingMessageRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isStreaming, chatHistory]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleGenerateLesson = () => {
    if (!promptVariables) {
      console.error('No prompt variables available');
      return;
    }
    // Use the consumeGenerator with the new signature that accepts promptVariables
    consumeGenerator({
      promptVariables: promptVariables,
    });
  };

  const lastUserMessageIndex = useMemo(() => {
    if (chatHistory.length === 0) return undefined;
    for (let i = chatHistory.length - 1; i >= 0; i--) {
      if (chatHistory[i].role === 'user') {
        return i;
      }
    }
  }, [chatHistory]);

  return (
    <Container $isChatHistory={chatHistory.length >= 1}>
      {/* Show loader when generating lesson */}
      {showLoader && (
        <LoaderContainer>
          <ShowLessonCreationProgressBar />
        </LoaderContainer>
      )}
      <BaseModal visible={showErrorModal} onCancel={() => setShowErrorModal(false)}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <IconWrapper height="120px" width="120px" style={{ marginTop: 20 }}>
            <FoldedHandsSVG />
          </IconWrapper>

          <ErrorMessage>
            <>
              <ErrorMessage style={{ fontWeight: 800 }}>
                Uh-oh! Something went wrong on our end
              </ErrorMessage>

              <ErrorMessage style={{ fontSize: '16px' }}>
                {errorConsumingGenerator?.customMessage ||
                  'Please try again or contact support if the issue persists'}
              </ErrorMessage>
            </>
          </ErrorMessage>
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <Button width="120px" onClick={() => setShowErrorModal(false)}>
              Ok
            </Button>
            <Button
              width="120px"
              onClick={() => {
                const errorMessage =
                  errorConsumingGenerator?.customMessage || 'Unknown error occurred';
                showNewMessages(
                  `I'm having issues with lesson generation and need help.\n\nError: ${errorMessage}`,
                );
                setShowErrorModal(false);
              }}
            >
              Get Help
            </Button>
          </div>
        </div>
      </BaseModal>
      {!showLoader && (
        <>
          <>
            <Title $hasHistory={chatHistory.length >= 1}>
              What will you teach today?
            </Title>
          </>

          <ChatContainer $hasHistory={chatHistory.length >= 1} ref={chatScrollRef}>
            {chatHistory.length >= 1 && (
              <ChatHistory>
                {chatHistory.map((message, index) => (
                  <ChatMessage
                    key={index}
                    role={message.role}
                    ref={index === lastUserMessageIndex ? lastUserMessageRef : null}
                  >
                    {message.role === 'assistant' && (
                      <AgentBranding>
                        <IconWrapper
                          height="16px"
                          width="16px"
                          style={{ marginBottom: 2 }}
                        >
                          <CuripodAgentSvg />
                        </IconWrapper>
                        <span
                          style={{
                            fontWeight: 'bold',
                            fontSize: 16,
                            color: theme.colors.black,
                            lineHeight: '16px',
                          }}
                        >
                          Curipod
                        </span>
                      </AgentBranding>
                    )}
                    <ChatMessageViewer content={message.content} />
                  </ChatMessage>
                ))}

                <div ref={streamingMessageRef}>
                  {streamData.currentContent && !streamData.isComplete && (
                    <ChatMessage role="assistant">
                      <AgentBranding>
                        <IconWrapper
                          height="16px"
                          width="16px"
                          style={{ marginBottom: 2 }}
                        >
                          <CuripodAgentSvg />
                        </IconWrapper>
                        <span
                          style={{
                            fontWeight: 'bold',
                            fontSize: 16,
                            color: theme.colors.black,
                            lineHeight: '16px',
                          }}
                        >
                          Curipod
                        </span>
                      </AgentBranding>
                      <ChatMessageViewer content={streamData.currentContent} />
                    </ChatMessage>
                  )}

                  {isStreaming && streamData.state && (
                    <ChatMessage role="assistant">
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: '20px auto',
                          gap: 5,
                          alignItems: 'center',
                        }}
                      >
                        <IconWrapper height="16px" width="16px">
                          <BrainSvg />
                        </IconWrapper>
                        <ThinkingMessage>{streamData.state}</ThinkingMessage>
                      </div>
                    </ChatMessage>
                  )}
                </div>

                {/* Large spacer at the bottom to push content up */}
                <div
                  aria-hidden="true"
                  style={{
                    height: spacerHeight,
                    flexShrink: 0,
                  }}
                />
                {/* Div used only as a scroll ref to scroll into view */}
                <div ref={scrollRef} />
              </ChatHistory>
            )}
          </ChatContainer>
          <FormContainer
            style={{
              maxHeight: chatHistory.length >= 1 ? 'fit-content' : 'unset',
              paddingBottom: chatHistory.length >= 1 ? '10px' : '20px',
            }}
          >
            <InputContainer
              $isDisabled={isStreaming}
              $hasChatHistory={chatHistory.length >= 1}
              $isTextAreaFocused={isTextAreaFocused}
              $isHovered={isInputContainerHovered}
              onMouseEnter={() => setIsInputContainerHovered(true)}
              onMouseLeave={() => setIsInputContainerHovered(false)}
              onClick={() => {
                if (inputRef.current) {
                  inputRef.current.focus();
                }
              }}
            >
              <TextArea
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onFocus={() => setIsTextAreaFocused(true)}
                onBlur={() => setIsTextAreaFocused(false)}
                onKeyDown={handleKeyDown}
                placeholder={
                  chatHistory.length === 0 && !isStreaming
                    ? 'Start by describing your lesson. For example: Grade 5, ELA, Characterization'
                    : isStreaming || isObjectiveReady
                    ? '' // Empty when streaming to show animated overlay
                    : 'Reply to Curipod AI...'
                }
                autoSize={{ minRows: 2, maxRows: 10 }}
                ref={inputRef}
                disabled={isStreaming || !!error || isObjectiveReady}
                textAreaStyled={{
                  fontSize: 18,
                  fontWeight: 600,
                  padding: '10px',
                  borderRadius: 0,
                  border: 'none',
                  backgroundColor: 'transparent',
                }}
                style={{
                  minWidth: '100%',
                }}
              />

              {isStreaming && (
                <div
                  style={{
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    fontSize: 18,
                    fontWeight: 600,
                    pointerEvents: 'none',
                    color: theme.colors.greyDark,
                  }}
                >
                  Generating
                  <LoadingDots>...</LoadingDots>
                </div>
              )}

              {!chatHistory.length && (
                <ButtonsBar>
                  <Dropdown
                    overlay={
                      <Menu>
                        <StyledMenuItem
                          key="presentation"
                          onClick={() => {
                            metrics.logEvent('CreatePage.SelectPresentation', {
                              promptInput: inputValue,
                            });
                            dispatchAndSync({
                              type: 'SET_MAIN_LESSON_CATEGORY',
                              payload: 'upload',
                            });
                          }}
                        >
                          <IconWrapper height="16px" width="16px">
                            <MediaIconSvg />
                          </IconWrapper>
                          <span>Presentation (PPT, PDF, Google)</span>
                        </StyledMenuItem>
                        <Tooltip title="Coming Soon" placement="right">
                          <StyledMenuItem
                            key="youtube"
                            onClick={() => {
                              // Do nothing - disabled item
                            }}
                            style={{ opacity: 0.5, cursor: 'not-allowed' }}
                          >
                            <IconWrapper height="16px" width="16px">
                              <YoutubeSvg />
                            </IconWrapper>
                            <span>Youtube link</span>
                          </StyledMenuItem>
                        </Tooltip>
                        <Tooltip title="Coming Soon" placement="right">
                          <StyledMenuItem
                            key="upload"
                            onClick={() => {
                              // Do nothing - disabled item
                            }}
                            style={{ opacity: 0.5, cursor: 'not-allowed' }}
                          >
                            <IconWrapper height="16px" width="16px">
                              <UploadsSvg />
                            </IconWrapper>
                            <span>Upload file</span>
                          </StyledMenuItem>
                        </Tooltip>
                        <Tooltip title="Coming Soon" placement="right">
                          <StyledMenuItem
                            key="text"
                            onClick={() => {
                              // Do nothing - disabled item
                            }}
                            style={{ opacity: 0.5, cursor: 'not-allowed' }}
                          >
                            <IconWrapper height="16px" width="16px">
                              <PasteSvg />
                            </IconWrapper>
                            <span>Paste in text</span>
                          </StyledMenuItem>
                        </Tooltip>
                      </Menu>
                    }
                    trigger={['click']}
                  >
                    <Tooltip title="Add Resources" placement="bottom">
                      <AddResourcesDropdownButton type="ghost">
                        <IconWrapper height="20px" width="20px">
                          <PlusSvg />
                        </IconWrapper>
                      </AddResourcesDropdownButton>
                    </Tooltip>
                  </Dropdown>
                  <div
                    style={{
                      display: 'flex',
                      gap: 10,
                      width: 'fit-content',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      marginLeft: 'auto',
                    }}
                  >
                    <Dropdown
                      overlay={
                        <Menu>
                          <Menu.Item
                            style={{
                              background: theme.colors.lightestGreen,
                              color: theme.colors.black,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 10,
                            }}
                            key="fullLesson"
                            disabled={true}
                          >
                            <span
                              style={{
                                width: '100%',
                                minWidth: '200px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 10,
                              }}
                            >
                              Full Lesson
                              <CheckSvg />
                            </span>
                          </Menu.Item>
                          <Menu.Item
                            key="quickWrites"
                            onClick={() => {
                              metrics.logEvent('CreatePage.SelectQuickWrites', {
                                promptInput: inputValue,
                              });
                              dispatchAndSync({
                                type: 'SET_MAIN_LESSON_CATEGORY',
                                payload: 'writing_skills',
                              });
                            }}
                          >
                            Quick Writes
                          </Menu.Item>
                          <Menu.Item
                            key="testPrep"
                            onClick={() => {
                              metrics.logEvent('CreatePage.SelectTestPrep', {
                                promptInput: inputValue,
                              });
                              dispatchAndSync({
                                type: 'SET_MAIN_LESSON_CATEGORY',
                                payload: 'test_prep',
                              });
                            }}
                          >
                            Test Prep
                          </Menu.Item>
                        </Menu>
                      }
                      trigger={['click']}
                    >
                      <LessonTypeDropdownButton type="ghost">
                        Full lesson{' '}
                        <IconWrapper
                          style={{ transform: 'rotate(-90deg)' }}
                          height="16px"
                          width="16px"
                        >
                          <ArrowLeftSvg />
                        </IconWrapper>
                      </LessonTypeDropdownButton>
                    </Dropdown>

                    {!isStreaming && !error && (
                      <SubmitButton
                        onClick={handleSubmit}
                        disabled={!inputValue.trim() || isStreaming}
                      >
                        <IconWrapper
                          style={{ transform: 'rotate(-90deg)' }}
                          height="20px"
                          width="20px"
                          color={theme.colors.white}
                        >
                          <ArrowDownSvg />
                        </IconWrapper>
                      </SubmitButton>
                    )}
                  </div>
                </ButtonsBar>
              )}
              {chatHistory.length >= 1 && !isStreaming && !error && (
                <SubmitButton
                  onClick={handleSubmit}
                  disabled={!inputValue.trim() || isStreaming}
                  styles={{
                    marginLeft: 'auto',
                    visibility: isObjectiveReady ? 'hidden' : 'visible',
                  }}
                >
                  <IconWrapper
                    style={{ transform: 'rotate(180deg)' }}
                    height="20px"
                    width="20px"
                    color={theme.colors.white}
                  >
                    <ArrowDownSvg />
                  </IconWrapper>
                </SubmitButton>
              )}
              {isStreaming && (
                <Button
                  type="ghost"
                  onClick={() => stopStream()}
                  icon={
                    <IconWrapper height="14px" width="14px" color={theme.colors.greyDark}>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 14 14"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12.6 0H1.4C0.6286 0 0 0.6286 0 1.4V12.6C0 13.3714 0.6286 14 1.4 14H12.6C13.3714 14 14 13.3714 14 12.6V1.4C14 0.6286 13.3714 0 12.6 0Z"
                          fill="currentColor"
                        />
                      </svg>
                    </IconWrapper>
                  }
                  styles={{
                    marginLeft: 'auto',
                    width: 'fit-content',
                    borderRadius: '20px',
                    height: '40px',
                  }}
                >
                  Stop
                </Button>
              )}
              {/* Show Generate Lesson options when objective is ready */}
              {isObjectiveReady && chatHistory.length > 0 && (
                <LessonReadyButtons>
                  <Button
                    type="ghost"
                    onClick={() => {
                      // Focus back on the input to continue chatting
                      if (inputRef.current) {
                        setIsObjectiveReady(false);
                        inputRef.current.focus();
                      }
                    }}
                  >
                    Keep chatting
                  </Button>
                  <GenerateLessonButton
                    onClick={handleGenerateLesson}
                    loading={isGeneratingLesson}
                    icon={
                      <IconWrapper height="20px" width="20px">
                        <CuripodAISolidStarsSvg />
                      </IconWrapper>
                    }
                    disabled={isGeneratingLesson}
                  >
                    Generate lesson
                  </GenerateLessonButton>
                </LessonReadyButtons>
              )}
            </InputContainer>
            {chatHistory.length === 0 && !error && (
              <SuggestionsWrapper>
                <SuggestionsContainer $isVisible={showSuggestions}>
                  <SuggestionsGrid>
                    <ShuffleButton
                      onClick={shuffleSuggestions}
                      title="Get new suggestions"
                    >
                      <IconWrapper
                        height="16px"
                        width="16px"
                        color={theme.colors.greyDark}
                      >
                        <ShuffleSvg />
                      </IconWrapper>
                    </ShuffleButton>
                    {randomSuggestions.map((suggestion, index) => (
                      <SuggestionButton
                        key={index}
                        onClick={() => {
                          metrics.logEvent('CreatePage.SelectLessonIdea', {
                            lessonIdea: suggestion.fullValue,
                          });
                          handleSuggestionClick(suggestion.fullValue);
                        }}
                      >
                        {suggestion.summary}
                      </SuggestionButton>
                    ))}
                  </SuggestionsGrid>
                </SuggestionsContainer>

                <CollapseButton
                  type="linkStyle"
                  onClick={() => {
                    if (!showSuggestions) {
                      metrics.logEvent('CreatePage.ShowLessonIdeas');
                      setShowSuggestions(true);
                    } else {
                      metrics.logEvent('CreatePage.HideLessonIdeas');
                      setShowSuggestions(false);
                    }
                  }}
                >
                  {showSuggestions ? 'Hide Lesson Ideas' : 'Show Lesson Ideas'}
                </CollapseButton>
              </SuggestionsWrapper>
            )}
          </FormContainer>
        </>
      )}
    </Container>
  );
};

const ErrorMessage = styled.h4`
  font-weight: bold;
  width: 100%;
  text-align: center;
  margin-top: 20px;
  font-size: 18px;

  p {
    margin-top: 10px;
    font-size: 14px;
  }
`;

const Container = styled.div<{ $isChatHistory: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: ${({ $isChatHistory }) => ($isChatHistory ? '0px' : '20vh')};
  height: 100vh;
  text-align: center;
  width: 100%;
  margin: 0 auto;
  transition: all 0.8s ease-in-out;
  position: relative;
  z-index: 2;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 20px;
    background: #fbf7f2;
    background: linear-gradient(
      0deg,
      rgba(251, 247, 242, 0) 0%,
      rgba(251, 247, 242, 1) 100%
    );
  }
`;

const Title = styled.h1<{ $hasHistory: boolean }>`
  font-size: 48px;
  font-weight: 600;
  color: ${theme.colors.black};
  margin-bottom: 25px;
  opacity: ${({ $hasHistory }) => ($hasHistory ? '0' : '1')};
  margin-top: ${({ $hasHistory }) => ($hasHistory ? '-100px' : '0px')};
  transition: all 0.8s ease-in-out;

  @media (max-width: 700px) {
    font-size: 28px;
  }
`;

const ChatContainer = styled.div<{ $hasHistory: boolean }>`
  width: 100%;
  flex: ${({ $hasHistory }) => ($hasHistory ? '1' : '0')};
  margin-bottom: ${({ $hasHistory }) => ($hasHistory ? '0px' : '20px')};
  padding: 20px 20px 0px 20px;
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  opacity: ${({ $hasHistory }) => ($hasHistory ? '1' : '0')};
  transition: flex 0.8s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease-out 0.2s;
`;

const ChatHistory = styled.div`
  flex: 1;
  // overflow-y: auto;
  display: flex;
  max-width: 800px;
  width: 100%;
  margin: 0 auto;
  flex-direction: column;
  gap: 12px;
  padding: 8px;
  min-height: 0;
`;

const ChatMessage = styled.div<{ role: 'user' | 'assistant' }>`
  padding: 12px;
  border-radius: 8px;
  max-width: 95%;
  align-self: ${props => (props.role === 'user' ? 'flex-end' : 'flex-start')};
  background-color: ${props =>
    props.role === 'user' ? theme.colors.paleBeige : theme.colors.lessTranslucentWhite};
  color: ${props => (props.role === 'user' ? theme.colors.black : theme.colors.black)};
  font-size: 16px;
  font-weight: 600;
`;

const AgentBranding = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
`;

const InputContainer = styled.div<{
  $hasChatHistory: boolean;
  $isTextAreaFocused: boolean;
  $isHovered: boolean;
  $isDisabled: boolean;
}>`
  width: 100%;
  max-width: 800px;
  margin-bottom: ${({ $hasChatHistory }) => ($hasChatHistory ? '0px' : '20px')};
  position: relative;
  background-color: ${({ $isDisabled }) =>
    $isDisabled ? theme.colors.greyLight : theme.colors.white};
  box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.17), 0px 0.3px 30px rgba(0, 0, 0, 0.17);
  border-radius: 10px;
  padding: 10px;
  border: 1px solid transparent;
  cursor: text;
  transition: all 0.5s ease;

  ${({ $isHovered }) =>
    $isHovered &&
    `
    border: 1px solid ${theme.colors.greyLight};
    box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.23), 0px 0.3px 10px rgba(0, 0, 0, 0.21);
  `}
  ${({ $isTextAreaFocused }) =>
    $isTextAreaFocused &&
    `
    border: 1px solid ${theme.colors.grey};
    box-shadow: 0px 1.6px 3.6px rgba(0, 0, 0, 0.23), 0px 0.3px 10px rgba(0, 0, 0, 0.21);
  `}
`;

const FormContainer = styled.div`
  width: 100%;
  padding: 0px 20px 20px 20px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const SubmitButton = styled(Button)`
  width: fit-content;
  height: 30px;
  width: 30px;
  border-radius: 20px;
  padding: 20px;
  background: ${theme.colors.orangeGradient};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  font-size: 16px;
  font-weight: 600;
  color: ${theme.colors.white} !important;
  gap: 10px;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
    background: linear-gradient(45deg, #ffa55b, #ffc107);
    background-size: 400% 400%;
    animation: ${gradientShift} 2s ease infinite;
  }

  &:active {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    background: ${theme.colors.orangeGradient} !important;
    cursor: not-allowed;
  }

  div {
    cursor: inherit;
  }

  svg {
    margin-right: 0px;
  }
`;

const ThinkingMessage = styled.p`
  text-transform: capitalize;
  display: flex;
  align-items: center;
  margin-bottom: 0;
  gap: 8px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  background: linear-gradient(
    to left,
    #b9b9b9 20%,
    #393939 30%,
    #393939 70%,
    #b9b9b9 80%
  );
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-fill-color: transparent;
  background-size: 200% auto;
  animation: textShine 2.5s linear infinite;
  @keyframes textShine {
    0% {
      background-position: 100% 50%;
    }
    100% {
      background-position: -100% 50%;
    }
  }
`;

const SuggestionsWrapper = styled.div<{ $isVisible?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const SuggestionsContainer = styled.div<{ $isVisible?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  opacity: ${({ $isVisible }) => ($isVisible ? '1' : '0')};
  max-height: ${({ $isVisible }) => ($isVisible ? '500px' : '0')};
  transition: all 0.3s linear;
  overflow: hidden;
`;

const SuggestionsGrid = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
`;

const CollapseButton = styled(Button)`
  font-size: 14px;
  font-weight: 500;
  padding: 5px 10px;
  color: ${theme.colors.greyDark};
  border: none;
  border-radius: 8px;
  background-color: transparent;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
    color: ${theme.colors.greyVeryDark};
  }
`;

const SuggestionButton = styled.button`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.greyVeryDark};
  padding: 5px 16px;
  border: 1px solid ${theme.colors.grey};
  border-radius: 10px;
  background-color: rgba(251, 247, 242, 0.8);
  transition: all 0.2s ease;
  cursor: pointer;
  white-space: nowrap;
  width: 200px;

  &:hover {
    background-color: ${theme.colors.paleBeige};
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
  }
`;

const ShuffleButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(251, 247, 242, 0.8);
  border: 1px solid ${theme.colors.grey};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.paleBeige};
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const LoaderContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
`;

const ButtonsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
  justify-content: space-between;
  align-items: flex-end;
`;

const DropdownButton = styled(Button)`
  border: 1px solid ${theme.colors.greyLight};
  box-shadow: none;
  padding: 10px 15px;
  border-radius: 20px;
  background: none;
  height: 30px;
  display: flex;
  align-items: space-between;
  justify-content: center;
  gap: 5px;
  transition: all 0.2s ease;

  span {
    font-weight: 400;
  }
  svg {
    margin: 0px;
    height: 18px;
    width: 18px;
  }
`;

const LessonTypeDropdownButton = styled(DropdownButton)`
  border: 1px solid transparent;
  padding: 10px;
  height: 35px;
  border-radius: 10px;

  &:hover,
  &:focus {
    background-color: ${theme.colors.greyLight};
    border: 1px solid ${theme.colors.grey};
  }
`;

const AddResourcesDropdownButton = styled(DropdownButton)`
  height: 30px;
  width: 30px;

  &:hover {
    box-shadow: ${theme.effects.floatingCreatorElementShadow};
    border: 1px solid transparent;
  }
`;

const ObjectiveRefinementComponent = () => {
  const session = useSession();
  const { tenantId } = useRouterMatch();
  const metrics = useMetrics();
  const dispatchAndSync = useDynamicLessonStateContext(c => c.dispatchAndSync);
  const mainLessonCategory = useDynamicLessonStateContext(
    c => c.dynamicLessonState.mainLessonCategory,
  );
  const grade = useDynamicLessonStateContext(c => c.dynamicLessonState.grade);

  const generatorFilterCategory = useDynamicLessonStateContext(
    c => c.dynamicLessonState.generatorFilterCategory,
  );
  const prompt = useDynamicLessonStateContext(c => c.dynamicLessonState.prompt);
  const uploadedFile = useDynamicLessonStateContext(
    c => c.dynamicLessonState.uploadedFile,
  );
  const lessonIntent = useDynamicLessonStateContext(
    c => c.dynamicLessonState.lessonIntent,
  );
  const subject = useDynamicLessonStateContext(c => c.dynamicLessonState.subject);
  const currentPage = useDynamicLessonStateContext(
    c => c.dynamicLessonState.selectedPage,
  );
  // Get chat history from context
  const chatHistory = useDynamicLessonStateContext(c => c.dynamicLessonState.chatHistory);
  const isDesktop = useBreakPoint(700);
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const { testPrepState, dispatch } = useTestPrepState(userAccessBadge);
  const { selectedPage } = testPrepState;
  const presetState = userAccessBadge?.organization?.region;
  const tenantSubjects = userAccessBadge?.user.profileInfo?.subjects;
  const gameAPI = React.useMemo(() => {
    if (session.data?.idToken) {
      return new GameAPI(session.data.idToken as string);
    }
    return undefined;
  }, [session.data?.idToken]);

  const onSelectGeneratorFilterCategory = (categoryId: string) => {
    metrics.logEvent('CreatePage.SelectCategory.Clicked', {
      selectedCategory: categoryId,
      selectedLessonCategory: mainLessonCategory,
      schoolState: presetState,
      tenantSubjects,
    });
    dispatchAndSync({
      type: 'SET_GENERATOR_FILTER_CATEGORY',
      payload: categoryId as string,
    });
  };

  const handleClearFilters = () => {
    if (mainLessonCategory === 'full_lesson') {
      if (currentPage === Page.SubjectSelection) {
        dispatchAndSync({ type: 'RESET_ALL' });
        return;
      }
      if (currentPage === Page.LessonIntentSelection) {
        dispatchAndSync({ type: 'RESET_SUBJECTS' });
        return;
      }

      if (currentPage === Page.LessonGoals) {
        // If we're in quickWrites and have a generator category selected, reset just that first
        if (lessonIntent?.id === 'quickWrites' && generatorFilterCategory && !isDesktop) {
          dispatchAndSync({ type: 'SET_GENERATOR_FILTER_CATEGORY', payload: '' });
          return;
        }
        dispatchAndSync({ type: 'RESET_LESSON_INTENT' });
        return;
      }

      if (currentPage === Page.Generate) {
        const pageToSkipTo = lessonIntent?.hasMoreDetails
          ? Page.LessonGoals
          : subject === 'advancedPrompting'
          ? Page.SubjectSelection
          : Page.LessonIntentSelection;
        dispatchAndSync({ type: 'SET_PAGE', payload: pageToSkipTo });
        return;
      }
    }

    if (mainLessonCategory === 'test_prep') {
      //if testprep
      if (selectedPage === 1) {
        dispatchAndSync({ type: 'RESET_ALL' });
        dispatch({ type: 'RESET' });
        return;
      }
      if (selectedPage === 2) {
        dispatch({ type: 'GO_BACK', payload: 1 });
        return;
      }
      if (selectedPage === 3) {
        dispatch({ type: 'GO_BACK', payload: 2 });
        return;
      }
      return;
    }

    dispatchAndSync({ type: 'RESET_ALL' });
    return;
  };

  if (session.status === 'loading') {
    return <div>Loading...</div>;
  }

  if (session.status === 'unauthenticated') {
    return <div>Please sign in to access this feature.</div>;
  }

  if (!gameAPI) {
    return (
      <CenteredContainer>
        <Loader />
      </CenteredContainer>
    );
  }

  return (
    <PageContainer $isCentered={!chatHistory.length}>
      {!chatHistory.length && (
        <CuripodAIIconContainer>
          <CuripodAIStarsSvg />
          Curipod AI
        </CuripodAIIconContainer>
      )}
      <SocketProvider gameAPI={gameAPI}>
        <CustomIntercomButton
          icon={
            <IconWrapper height="20px" width="20px">
              <QuestionMarkSvg />
            </IconWrapper>
          }
        />
        <HideNextJsLoader />
        <BackgroundContainer>
          <MagicBackground />
        </BackgroundContainer>
        <BackButtonContainer>
          {!mainLessonCategory && !chatHistory.length ? (
            <Link href={`/${tenantId}/lessons`} passHref>
              <a>
                <BackButton
                  onClick={() => {
                    metrics.logEvent('CreatePage.GoHome.Clicked', {
                      prompt: prompt,
                      grade: grade,
                      subject: subject,
                      fileType: uploadedFile?.fileType,
                      currentPage: mainLessonCategory,
                      lessonIntent: lessonIntent?.id,
                      schoolState: presetState,
                    });
                  }}
                  type="primary"
                  icon={<HomeFilledSvg />}
                  styles={{
                    height: 42,
                    width: 110,
                  }}
                >
                  Home
                </BackButton>
              </a>
            </Link>
          ) : chatHistory.length ? (
            <BackButton
              $iconHeight={30}
              $iconWidth={30}
              type="primary"
              styles={{
                padding: '10px',
                height: 42,
              }}
              notext={true}
              icon={
                <IconWrapper height={'30px'} width={'30px'}>
                  <ArrowLeftSvg style={{ marginRight: '0px' }} />
                </IconWrapper>
              }
              onClick={() => {
                dispatchAndSync({ type: 'RESET_ALL' });
              }}
            ></BackButton>
          ) : (
            <BackButton
              type="primary"
              styles={{
                padding: '10px 20px 10px 10px',
                height: 42,
                width: 110,
              }}
              onClick={() => {
                metrics.logEvent('CreatePage.GoBack.Clicked', {
                  prompt: prompt,
                  grade: grade,
                  subject: subject,
                  fileType: uploadedFile?.fileType,
                  currentPage: mainLessonCategory,
                  lessonIntent: lessonIntent?.id,
                  schoolState: presetState,
                });
                handleClearFilters();
              }}
              icon={
                <IconWrapper height={'30px'} width={'30px'}>
                  <ArrowLeftSvg />
                </IconWrapper>
              }
            >
              Back
            </BackButton>
          )}
        </BackButtonContainer>
        {mainLessonCategory === 'test_prep' && lessonCategories['test_prep'].categories && (
          <div style={{ zIndex: 1, position: 'relative', paddingTop: '80px' }}>
            <TestPrepTab testPrepState={testPrepState} dispatch={dispatch} />
          </div>
        )}
        {mainLessonCategory === 'writing_skills' && (
          <div style={{ zIndex: 1, position: 'relative', paddingTop: '80px' }}>
            {isDesktop ? (
              <SubPageLayout pageTitle="Choose your quick write lesson">
                <QuickWritesContainer>
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '20px',
                      width: '100%',
                      alignItems: 'center',
                    }}
                  >
                    <CategoryList
                      handleSelectCategory={(categoryId: string) => {
                        onSelectGeneratorFilterCategory(categoryId);
                      }}
                      visibleCategories={writingSkills.categories || []}
                      activeCategoryId={generatorFilterCategory}
                    />
                    {generatorFilterCategory && (
                      <FindCategorizedLessonsTab
                        lessonCategoryId={generatorFilterCategory}
                        selectedCategory={generatorFilterCategory}
                      />
                    )}
                  </div>
                </QuickWritesContainer>
              </SubPageLayout>
            ) : (
              <>
                {!generatorFilterCategory ? (
                  <SubPageLayout pageTitle="Choose a Category">
                    <QuickWritesContainer>
                      <CategoryList
                        handleSelectCategory={(categoryId: string) => {
                          onSelectGeneratorFilterCategory(categoryId);
                        }}
                        visibleCategories={writingSkills.categories || []}
                        activeCategoryId={generatorFilterCategory}
                      />
                    </QuickWritesContainer>
                  </SubPageLayout>
                ) : (
                  <SubPageLayout pageTitle="Quick Writes">
                    <QuickWritesContainer>
                      <FindCategorizedLessonsTab
                        categoryTitle={
                          writingSkills.categories?.find(
                            category => category.id === generatorFilterCategory,
                          )?.title || ''
                        }
                        lessonCategoryId={generatorFilterCategory}
                        selectedCategory={generatorFilterCategory}
                      />
                    </QuickWritesContainer>
                  </SubPageLayout>
                )}
              </>
            )}
          </div>
        )}
        {mainLessonCategory === 'upload' && (
          <div style={{ zIndex: 1, position: 'relative', paddingTop: '80px' }}>
            <CurifyTab />
          </div>
        )}

        {!mainLessonCategory && (
          <ObjectiveRefinementWithSocket
            chatHistory={chatHistory}
            dispatchAndSync={dispatchAndSync}
          />
        )}
      </SocketProvider>
    </PageContainer>
  );
};

const ObjectiveRefinement = () => {
  return <ObjectiveRefinementComponent />;
};

export default ObjectiveRefinement;

const BackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 1;
  svg {
    height: 98vh; // 99 and 100 adds a scroll...
    width: 100vw;
  }
`;
const BackButton = styled(Button)<{ $iconHeight?: number; $iconWidth?: number }>`
  padding: 10px 20px;
  background-color: ${theme.colors.white};
  box-shadow: ${theme.effects.magicFloatingShadow};
  color: ${theme.colors.black};

  &:hover,
  &:focus,
  &:active {
    background-color: ${theme.colors.greyLight};
    color: ${theme.colors.black};
    box-shadow: ${theme.effects.defaultShadow};
  }

  svg {
    height: ${({ $iconHeight }) => $iconHeight ?? '16'}px;
    width: ${({ $iconWidth }) => $iconWidth ?? '16'}px;
  }
`;
const BackButtonContainer = styled.div`
  position: absolute;
  top: 20px;
  margin-left: 20px;
  width: fit-content;
  z-index: 3;
`;
const PageContainer = styled.div<{ $isCentered?: boolean }>`
  position: relative;
  height: 100%;
  width: 100%;
  background: #ffc698;
  background: linear-gradient(
    180deg,
    rgb(255, 245, 238) 0%,
    rgba(255, 198, 152, 0.8) 25%,
    rgba(255, 198, 152, 0.8) 50%,
    rgb(255, 245, 238) 100%
  );
`;
const CuripodAIIconContainer = styled.div`
  position: absolute;
  cursor: default;
  top: 20px;
  right: 20px;
  z-index: 3;
  display: flex;
  align-items: center;
  flex-direction: column;
  font-size: 14px;
  font-weight: 600;
  width: fit-content;
  color: ${theme.colors.orange};
  svg {
    width: 100%;
    height: auto;
  }
`;

const QuickWritesContainer = styled.div`
  margin: 0 auto;
  width: 80%;
  display: grid;
  gap: 20px;

  @media (max-width: 1000px) {
    width: 100%;
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const StyledMenuItem = styled(Menu.Item)`
  & > span {
    display: grid !important;
    grid-template-columns: 24px 1fr;
    gap: 12px;
    align-items: center;

    &:hover {
      background-color: ${theme.colors.greyLight} !important;
    }

    .ant-dropdown-menu-item-icon {
      margin-right: 0 !important;
    }
  }
`;
